(function () {
  const initialized = new WeakSet();

  function uniqueButtons(root, selector, attr) {
    const seen = new Set();
    root.querySelectorAll(selector).forEach((button) => {
      const value = button.getAttribute(attr) || '';
      if (seen.has(value)) button.remove();
      else seen.add(value);
    });
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return '$' + ((Number(cents) || 0) / 100).toFixed(2);
    }
  }

  function isPreviewable(file) {
    return Boolean(file && (file.type || '').startsWith('image/'));
  }

  function copyFileToForms(root, file) {
    root.querySelectorAll('[data-laser-form-artwork]').forEach((input) => {
      try {
        const dt = new DataTransfer();
        dt.items.add(file);
        input.files = dt.files;
      } catch (error) {
        // Browser may block programmatic FileList assignment; native product form remains disabled if unavailable.
      }
    });
  }

  function init(root) {
    if (!(root instanceof Element) || initialized.has(root)) return;
    initialized.add(root);

    const currency = root.dataset.currency || 'USD';
    const maxFileBytes = (Number(root.dataset.maxFileSize) || 20) * 1024 * 1024;
    const tabs = Array.from(root.querySelectorAll('[data-laser-family-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-laser-product-panel]'));
    const stage = root.querySelector('[data-laser-stage]');
    const objectImage = root.querySelector('[data-laser-object-image]');
    const objectFallback = root.querySelector('[data-laser-object-fallback]');
    const zone = root.querySelector('[data-laser-zone]');
    const mark = root.querySelector('[data-laser-mark]');
    const markPlaceholder = root.querySelector('[data-laser-mark-placeholder]');
    const artworkPreview = root.querySelector('[data-laser-artwork-preview]');
    const filePreview = root.querySelector('[data-laser-file-preview]');
    const fileType = root.querySelector('[data-laser-file-type]');
    const fileName = root.querySelector('[data-laser-file-name]');
    const uploadInput = root.querySelector('[data-laser-master-artwork]');
    const uploadStatus = root.querySelector('[data-laser-upload-status]');
    const uploadTitle = root.querySelector('[data-laser-upload-title]');
    const previewStatus = root.querySelector('[data-laser-preview-status]');
    const scaleInput = root.querySelector('[data-laser-scale]');
    const scaleValue = root.querySelector('[data-laser-scale-value]');
    const metaScale = root.querySelector('[data-laser-meta-scale]');
    const metaFamily = root.querySelector('[data-laser-meta-family]');
    const metaPlacement = root.querySelector('[data-laser-meta-placement]');
    const metaArea = root.querySelector('[data-laser-meta-area]');
    const centerButton = root.querySelector('[data-laser-center]');
    const resetButton = root.querySelector('[data-laser-reset]');
    const itemPhotoInput = root.querySelector('[data-laser-item-photo]');

    let activeFamily = root.dataset.defaultFamily || (tabs[0] && tabs[0].dataset.laserFamilyTab) || 'drinkware';
    let artworkFile = null;
    let artworkUrl = '';
    let itemPhotoUrl = '';
    let positionX = 50;
    let positionY = 50;
    let scale = 100;
    let dragState = null;

    const panelData = new Map();

    panels.forEach((panel) => {
      const family = panel.dataset.laserProductPanel;
      const json = panel.querySelector('[data-laser-product-json]');
      let product = null;
      if (json) {
        try { product = JSON.parse(json.textContent); } catch (error) { product = null; }
      }
      panelData.set(family, { panel, product });

      uniqueButtons(panel, '[data-laser-placement-choice]', 'data-laser-placement-choice');
      uniqueButtons(panel, '[data-laser-area-choice]', 'data-laser-area-choice');
      uniqueButtons(panel, '[data-laser-composite-a-choice]', 'data-laser-composite-a-choice');
      uniqueButtons(panel, '[data-laser-composite-b-choice]', 'data-laser-composite-b-choice');

      panel.querySelectorAll('[data-laser-option-value]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          selectOptionButton(panel, Number(button.dataset.optionIndex), button.dataset.laserOptionValue);
          updatePanel(panel);
        });
      });

      panel.querySelectorAll('[data-laser-placement-choice]').forEach((button) => {
        button.addEventListener('click', () => {
          setSplitChoice(panel, 'placement', button.dataset.laserPlacementChoice);
        });
      });

      panel.querySelectorAll('[data-laser-area-choice]').forEach((button) => {
        button.addEventListener('click', () => {
          setSplitChoice(panel, 'area', button.dataset.laserAreaChoice);
        });
      });

      panel.querySelectorAll('[data-laser-composite-a-choice]').forEach((button) => {
        button.addEventListener('click', () => {
          setCompositeChoice(panel, Number(button.closest('[data-laser-composite-option]').dataset.optionIndex), 'a', button.dataset.laserCompositeAChoice);
        });
      });

      panel.querySelectorAll('[data-laser-composite-b-choice]').forEach((button) => {
        button.addEventListener('click', () => {
          setCompositeChoice(panel, Number(button.closest('[data-laser-composite-option]').dataset.optionIndex), 'b', button.dataset.laserCompositeBChoice);
        });
      });

      initializeSplitControls(panel);
      if (product) updatePanel(panel);
    });

    function activePanel() {
      const entry = panelData.get(activeFamily);
      return entry ? entry.panel : null;
    }

    function activeProductData() {
      const entry = panelData.get(activeFamily);
      return entry ? entry.product : null;
    }

    function getSelections(panel) {
      const groups = Array.from(panel.querySelectorAll('[data-laser-option-group]'));
      return groups
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
        .map((group) => {
          const selected = group.querySelector('[data-laser-option-value].is-selected');
          return selected ? selected.dataset.laserOptionValue : '';
        });
    }

    function selectOptionButton(panel, optionIndex, value) {
      const group = panel.querySelector(`[data-laser-option-group][data-option-index="${optionIndex}"]`);
      if (!group) return;
      group.querySelectorAll('[data-laser-option-value]').forEach((button) => {
        const selected = button.dataset.laserOptionValue === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      syncSplitControls(panel, optionIndex);
      syncCompositeControls(panel, optionIndex);
    }

    function initializeSplitControls(panel) {
      panel.querySelectorAll('[data-laser-split-option]').forEach((split) => {
        const index = Number(split.dataset.optionIndex);
        syncSplitControls(panel, index);
      });
      panel.querySelectorAll('[data-laser-composite-option]').forEach((split) => {
        const index = Number(split.dataset.optionIndex);
        syncCompositeControls(panel, index);
      });
    }

    function splitLaserEncodedValue(raw) {
      const source = String(raw || '');
      if (source.includes(' - ')) return source.split(' - ');
      if (source.includes(' / ')) return source.split(' / ');
      return [source];
    }

    function splitValues(panel, optionIndex) {
      const group = panel.querySelector(`[data-laser-option-group][data-option-index="${optionIndex}"]`);
      if (!group) return [];
      return Array.from(group.querySelectorAll('[data-laser-option-value]')).map((button) => {
        const raw = button.dataset.laserOptionValue || '';
        const parts = splitLaserEncodedValue(raw);
        return { raw, placement: (parts[0] || raw).trim(), area: (parts[1] || parts[0] || raw).trim(), button };
      });
    }

    function currentSplit(panel, optionIndex) {
      const group = panel.querySelector(`[data-laser-option-group][data-option-index="${optionIndex}"]`);
      const selected = group && group.querySelector('[data-laser-option-value].is-selected');
      if (!selected) return null;
      const raw = selected.dataset.laserOptionValue || '';
      const parts = splitLaserEncodedValue(raw);
      return { raw, placement: (parts[0] || raw).trim(), area: (parts[1] || parts[0] || raw).trim() };
    }

    function syncSplitControls(panel, optionIndex) {
      const split = panel.querySelector(`[data-laser-split-option][data-option-index="${optionIndex}"]`);
      if (!split) return;
      const current = currentSplit(panel, optionIndex);
      if (!current) return;
      split.querySelectorAll('[data-laser-placement-choice]').forEach((button) => {
        const selected = button.dataset.laserPlacementChoice === current.placement;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      split.querySelectorAll('[data-laser-area-choice]').forEach((button) => {
        const selected = button.dataset.laserAreaChoice === current.area;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    function setSplitChoice(panel, axis, value) {
      const split = panel.querySelector('[data-laser-split-option]');
      if (!split) return;
      const optionIndex = Number(split.dataset.optionIndex);
      const current = currentSplit(panel, optionIndex);
      const choices = splitValues(panel, optionIndex);
      if (!current || !choices.length) return;

      let candidate = choices.find((choice) => axis === 'placement'
        ? choice.placement === value && choice.area === current.area
        : choice.area === value && choice.placement === current.placement);

      if (!candidate) {
        candidate = choices.find((choice) => axis === 'placement' ? choice.placement === value : choice.area === value);
      }
      if (!candidate || candidate.button.disabled) return;
      selectOptionButton(panel, optionIndex, candidate.raw);
      updatePanel(panel);
    }

    function compositeValues(panel, optionIndex) {
      const group = panel.querySelector(`[data-laser-option-group][data-option-index="${optionIndex}"]`);
      if (!group) return [];
      return Array.from(group.querySelectorAll('[data-laser-option-value]')).map((button) => {
        const raw = button.dataset.laserOptionValue || '';
        const parts = splitLaserEncodedValue(raw);
        return { raw, a: (parts[0] || raw).trim(), b: (parts[1] || parts[0] || raw).trim(), button };
      });
    }

    function currentComposite(panel, optionIndex) {
      const group = panel.querySelector(`[data-laser-option-group][data-option-index="${optionIndex}"]`);
      const selected = group && group.querySelector('[data-laser-option-value].is-selected');
      if (!selected) return null;
      const raw = selected.dataset.laserOptionValue || '';
      const parts = splitLaserEncodedValue(raw);
      return { raw, a: (parts[0] || raw).trim(), b: (parts[1] || parts[0] || raw).trim() };
    }

    function syncCompositeControls(panel, optionIndex) {
      const split = panel.querySelector(`[data-laser-composite-option][data-option-index="${optionIndex}"]`);
      if (!split) return;
      const current = currentComposite(panel, optionIndex);
      if (!current) return;
      split.querySelectorAll('[data-laser-composite-a-choice]').forEach((button) => {
        const selected = button.dataset.laserCompositeAChoice === current.a;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      split.querySelectorAll('[data-laser-composite-b-choice]').forEach((button) => {
        const selected = button.dataset.laserCompositeBChoice === current.b;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    function setCompositeChoice(panel, optionIndex, axis, value) {
      const current = currentComposite(panel, optionIndex);
      const choices = compositeValues(panel, optionIndex);
      if (!current || !choices.length) return;
      let candidate = choices.find((choice) => axis === 'a'
        ? choice.a === value && choice.b === current.b
        : choice.b === value && choice.a === current.a);
      if (!candidate) candidate = choices.find((choice) => axis === 'a' ? choice.a === value : choice.b === value);
      if (!candidate || candidate.button.disabled) return;
      selectOptionButton(panel, optionIndex, candidate.raw);
      updatePanel(panel);
    }

    function variantMatchesExcept(variant, selections, optionIndex, candidateValue) {
      if (!variant.available) return false;
      return selections.every((selection, index) => {
        const expected = index === optionIndex ? candidateValue : selection;
        return !expected || variant.options[index] === expected;
      });
    }

    function updateAvailability(panel, product, selections) {
      panel.querySelectorAll('[data-laser-option-group]').forEach((group) => {
        const optionIndex = Number(group.dataset.optionIndex);
        group.querySelectorAll('[data-laser-option-value]').forEach((button) => {
          const candidate = button.dataset.laserOptionValue;
          const possible = product.variants.some((variant) => variantMatchesExcept(variant, selections, optionIndex, candidate));
          button.disabled = !possible;
        });
      });

      panel.querySelectorAll('[data-laser-split-option]').forEach((split) => {
        const optionIndex = Number(split.dataset.optionIndex);
        const choices = splitValues(panel, optionIndex);
        const otherSelections = getSelections(panel);
        split.querySelectorAll('[data-laser-placement-choice]').forEach((button) => {
          const value = button.dataset.laserPlacementChoice;
          button.disabled = !choices.some((choice) => choice.placement === value && product.variants.some((variant) => variantMatchesExcept(variant, otherSelections, optionIndex, choice.raw)));
        });
        split.querySelectorAll('[data-laser-area-choice]').forEach((button) => {
          const value = button.dataset.laserAreaChoice;
          button.disabled = !choices.some((choice) => choice.area === value && product.variants.some((variant) => variantMatchesExcept(variant, otherSelections, optionIndex, choice.raw)));
        });
      });

      panel.querySelectorAll('[data-laser-composite-option]').forEach((split) => {
        const optionIndex = Number(split.dataset.optionIndex);
        const choices = compositeValues(panel, optionIndex);
        const otherSelections = getSelections(panel);
        split.querySelectorAll('[data-laser-composite-a-choice]').forEach((button) => {
          const value = button.dataset.laserCompositeAChoice;
          button.disabled = !choices.some((choice) => choice.a === value && product.variants.some((variant) => variantMatchesExcept(variant, otherSelections, optionIndex, choice.raw)));
        });
        split.querySelectorAll('[data-laser-composite-b-choice]').forEach((button) => {
          const value = button.dataset.laserCompositeBChoice;
          button.disabled = !choices.some((choice) => choice.b === value && product.variants.some((variant) => variantMatchesExcept(variant, otherSelections, optionIndex, choice.raw)));
        });
      });
    }

    function resolveVariant(panel, product) {
      const selections = getSelections(panel);
      return product.variants.find((variant) => variant.options.every((value, index) => value === selections[index])) || null;
    }

    function engravingMeta(panel) {
      const product = panelData.get(panel.dataset.laserProductPanel)?.product;
      if (!product) return { placement: panel.dataset.defaultPlacement || 'Review required', area: panel.dataset.defaultArea || 'Quoted to item' };
      const selections = getSelections(panel);
      let placement = panel.dataset.defaultPlacement || 'Front';
      let area = panel.dataset.defaultArea || 'Standard';

      panel.querySelectorAll('[data-laser-option-group]').forEach((group) => {
        const name = (group.dataset.optionName || '').toLowerCase();
        const index = Number(group.dataset.optionIndex);
        const value = selections[index] || '';
        if ((name.includes('engraving') || name.includes('package')) && value) {
          const parts = splitLaserEncodedValue(value);
          placement = (parts[0] || value).trim();
          area = (parts[1] || parts[0] || value).trim();
        } else if (name.includes('placement') && value) {
          placement = value;
        } else if ((name.includes('engraving size') || name.includes('engraving area')) && value) {
          area = value;
        }
      });
      return { placement, area };
    }

    function treatmentFor(panel) {
      if (!panel) return 'light';
      const product = panelData.get(panel.dataset.laserProductPanel)?.product;
      const selections = product ? getSelections(panel).join(' ').toLowerCase() : '';
      const family = panel.dataset.laserProductPanel;
      if (family === 'wood' || /walnut|maple|bamboo|wood/.test(selections)) return 'burn';
      if (family === 'leather' || /leather|hide/.test(selections)) return 'dark';
      if (family === 'acrylic' || /acrylic|frosted|clear/.test(selections)) return 'frosted';
      if (/black|navy|powder|coated|anodized/.test(selections)) return 'light';
      return panel.dataset.previewTreatment || 'light';
    }

    function updatePanel(panel) {
      const family = panel.dataset.laserProductPanel;
      const entry = panelData.get(family);
      if (!entry || !entry.product) {
        if (family === activeFamily) updatePreviewForActive();
        return;
      }

      const product = entry.product;
      const selections = getSelections(panel);
      updateAvailability(panel, product, selections);
      const variant = resolveVariant(panel, product);
      const idInput = panel.querySelector('[data-laser-variant-id]');
      const price = panel.querySelector('[data-laser-price]');
      const submitPrice = panel.querySelector('[data-laser-submit-price]');
      const summary = panel.querySelector('[data-laser-summary]');
      const submit = panel.querySelector('[data-laser-submit]');
      const status = panel.querySelector('[data-laser-form-status]');
      const meta = engravingMeta(panel);

      panel.querySelector('[data-laser-placement-property]')?.setAttribute('value', meta.placement);
      panel.querySelector('[data-laser-area-property]')?.setAttribute('value', meta.area);
      panel.querySelector('[data-laser-treatment-property]')?.setAttribute('value', treatmentFor(panel));

      if (variant) {
        if (idInput) idInput.value = variant.id;
        const money = formatMoney(variant.price, currency);
        if (price) price.textContent = money;
        if (submitPrice) submitPrice.textContent = money;
        if (summary) summary.textContent = variant.title;
      } else {
        if (idInput) idInput.value = '';
        if (price) price.textContent = 'Unavailable';
        if (submitPrice) submitPrice.textContent = '';
        if (summary) summary.textContent = 'This exact combination is unavailable';
      }

      const ready = Boolean(variant && variant.available && artworkFile);
      if (submit) submit.disabled = !ready;
      if (status) {
        status.classList.toggle('is-ready', ready);
        status.textContent = !artworkFile ? 'Upload artwork to continue.' : (!variant || !variant.available ? 'Choose an available configuration.' : 'Artwork attached. Ready to add to cart.');
      }

      if (family === activeFamily) updatePreviewForActive();
    }

    function applyZone(panel) {
      if (!zone || !panel) return;
      zone.style.left = `${Number(panel.dataset.zoneLeft) || 25}%`;
      zone.style.top = `${Number(panel.dataset.zoneTop) || 25}%`;
      zone.style.width = `${Number(panel.dataset.zoneWidth) || 50}%`;
      zone.style.height = `${Number(panel.dataset.zoneHeight) || 50}%`;
    }

    function applyObject(panel) {
      if (!stage || !panel) return;
      const family = panel.dataset.laserProductPanel || 'custom';
      stage.dataset.previewFamily = family;
      const src = family === 'custom' && itemPhotoUrl ? itemPhotoUrl : panel.dataset.previewSrc;
      if (src && objectImage) {
        objectImage.src = src;
        objectImage.alt = `${panel.dataset.familyLabel || 'Laser product'} preview`;
        objectImage.classList.remove('is-hidden');
        objectFallback?.classList.add('is-hidden');
      } else {
        objectImage?.classList.add('is-hidden');
        objectFallback?.classList.remove('is-hidden');
      }
    }

    function updatePreviewForActive() {
      const panel = activePanel();
      if (!panel || !stage) return;
      applyZone(panel);
      applyObject(panel);
      stage.dataset.previewTreatment = treatmentFor(panel);
      const meta = engravingMeta(panel);
      if (metaFamily) metaFamily.textContent = panel.dataset.familyLabel || activeFamily;
      if (metaPlacement) metaPlacement.textContent = meta.placement;
      if (metaArea) metaArea.textContent = meta.area;
      syncPreviewProperties(panel, meta);
    }

    function syncPreviewProperties(panel, meta) {
      panels.forEach((candidate) => {
        candidate.querySelector('[data-laser-position-x-property]')?.setAttribute('value', `${Math.round(positionX)}%`);
        candidate.querySelector('[data-laser-position-y-property]')?.setAttribute('value', `${Math.round(positionY)}%`);
        candidate.querySelector('[data-laser-scale-property]')?.setAttribute('value', `${scale}%`);
        if (artworkFile) candidate.querySelector('[data-laser-artwork-name-property]')?.setAttribute('value', artworkFile.name);
      });
      root.querySelector('[data-laser-quote-position]')?.setAttribute('value', `${Math.round(positionX)}% / ${Math.round(positionY)}%`);
      root.querySelector('[data-laser-quote-scale]')?.setAttribute('value', `${scale}%`);
      if (artworkFile) root.querySelector('[data-laser-quote-artwork-name]')?.setAttribute('value', artworkFile.name);
      if (panel) {
        panel.querySelector('[data-laser-placement-property]')?.setAttribute('value', meta.placement);
        panel.querySelector('[data-laser-area-property]')?.setAttribute('value', meta.area);
      }
    }

    function setFamily(family) {
      activeFamily = family;
      tabs.forEach((tab) => {
        const active = tab.dataset.laserFamilyTab === family;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      panels.forEach((panel) => {
        const active = panel.dataset.laserProductPanel === family;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      updatePreviewForActive();
    }

    tabs.forEach((tab) => tab.addEventListener('click', () => setFamily(tab.dataset.laserFamilyTab)));

    function setArtworkFile(file) {
      if (!file) return;
      if (file.size > maxFileBytes) {
        uploadStatus.textContent = `File is larger than ${root.dataset.maxFileSize || 20} MB.`;
        uploadTitle.textContent = 'Choose a smaller artwork file';
        return;
      }
      artworkFile = file;
      if (artworkUrl) URL.revokeObjectURL(artworkUrl);
      artworkUrl = '';
      copyFileToForms(root, file);
      if (uploadStatus) uploadStatus.textContent = file.name;
      if (uploadTitle) uploadTitle.textContent = 'Artwork attached';
      if (previewStatus) {
        previewStatus.textContent = 'Artwork ready';
        previewStatus.classList.add('is-ready');
      }
      markPlaceholder?.classList.add('is-hidden');
      filePreview?.classList.add('is-hidden');
      artworkPreview?.classList.add('is-hidden');

      if (isPreviewable(file)) {
        artworkUrl = URL.createObjectURL(file);
        if (artworkPreview) {
          artworkPreview.src = artworkUrl;
          artworkPreview.classList.remove('is-hidden');
        }
      } else {
        if (fileType) fileType.textContent = (file.name.split('.').pop() || 'FILE').toUpperCase();
        if (fileName) fileName.textContent = file.name;
        filePreview?.classList.remove('is-hidden');
      }

      panels.forEach((panel) => {
        panel.querySelector('[data-laser-artwork-name-property]')?.setAttribute('value', file.name);
        const entry = panelData.get(panel.dataset.laserProductPanel);
        if (entry && entry.product) updatePanel(panel);
      });
      root.querySelector('[data-laser-quote-artwork-name]')?.setAttribute('value', file.name);
      updatePreviewForActive();
    }

    uploadInput?.addEventListener('change', () => setArtworkFile(uploadInput.files && uploadInput.files[0]));

    const dropzone = root.querySelector('[data-laser-dropzone]');
    if (dropzone) {
      ['dragenter', 'dragover'].forEach((type) => dropzone.addEventListener(type, (event) => { event.preventDefault(); dropzone.classList.add('is-dragging'); }));
      ['dragleave', 'drop'].forEach((type) => dropzone.addEventListener(type, (event) => { event.preventDefault(); dropzone.classList.remove('is-dragging'); }));
      dropzone.addEventListener('drop', (event) => {
        const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (file) setArtworkFile(file);
      });
    }

    itemPhotoInput?.addEventListener('change', () => {
      const file = itemPhotoInput.files && itemPhotoInput.files[0];
      if (!file) return;
      if (itemPhotoUrl) URL.revokeObjectURL(itemPhotoUrl);
      itemPhotoUrl = URL.createObjectURL(file);
      root.querySelector('[data-laser-quote-photo-name]')?.setAttribute('value', file.name);
      if (activeFamily === 'custom') updatePreviewForActive();
    });

    function updateMarkPosition() {
      if (!mark) return;
      mark.style.left = `${positionX}%`;
      mark.style.top = `${positionY}%`;
      mark.style.setProperty('--laser-scale', String(scale / 100));
      if (scaleInput) scaleInput.value = String(scale);
      if (scaleValue) scaleValue.textContent = `${scale}%`;
      if (metaScale) metaScale.textContent = `${scale}%`;
      updatePreviewForActive();
    }

    scaleInput?.addEventListener('input', () => {
      scale = Number(scaleInput.value) || 100;
      updateMarkPosition();
    });

    centerButton?.addEventListener('click', () => { positionX = 50; positionY = 50; updateMarkPosition(); });
    resetButton?.addEventListener('click', () => { positionX = 50; positionY = 50; scale = 100; updateMarkPosition(); });

    mark?.addEventListener('pointerdown', (event) => {
      if (!zone) return;
      event.preventDefault();
      mark.setPointerCapture?.(event.pointerId);
      dragState = { pointerId: event.pointerId };
    });
    mark?.addEventListener('pointermove', (event) => {
      if (!dragState || dragState.pointerId !== event.pointerId || !zone) return;
      const rect = zone.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      positionX = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
      positionY = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
      updateMarkPosition();
    });
    function endDrag(event) {
      if (!dragState || dragState.pointerId !== event.pointerId) return;
      try { mark.releasePointerCapture?.(event.pointerId); } catch (error) {}
      dragState = null;
    }
    mark?.addEventListener('pointerup', endDrag);
    mark?.addEventListener('pointercancel', endDrag);
    mark?.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? 5 : 2;
      if (event.key === 'ArrowLeft') positionX = Math.max(0, positionX - step);
      else if (event.key === 'ArrowRight') positionX = Math.min(100, positionX + step);
      else if (event.key === 'ArrowUp') positionY = Math.max(0, positionY - step);
      else if (event.key === 'ArrowDown') positionY = Math.min(100, positionY + step);
      else return;
      event.preventDefault();
      updateMarkPosition();
    });

    setFamily(activeFamily);
    updateMarkPosition();

    root.addEventListener('shopify:block:select', (event) => {
      const blockPanel = event.target.closest && event.target.closest('[data-laser-product-panel]');
      if (blockPanel) setFamily(blockPanel.dataset.laserProductPanel);
    });

    root.addEventListener('far:laser:destroy', () => {
      if (artworkUrl) URL.revokeObjectURL(artworkUrl);
      if (itemPhotoUrl) URL.revokeObjectURL(itemPhotoUrl);
    }, { once: true });
  }

  function initializeWithin(scope) {
    if (scope instanceof Element && scope.matches('[data-far-laser]')) init(scope);
    scope.querySelectorAll?.('[data-far-laser]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  else initializeWithin(document);

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-far-laser]') ? event.target : event.target.querySelector?.('[data-far-laser]');
    root?.dispatchEvent(new CustomEvent('far:laser:destroy'));
  });
})();
